'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import type { Booking, Vehicle } from '@/types';
import {
  MOROCCAN_LOCATIONS,
  PIPELINE_STEPS,
  buildWhatsAppLink,
  bookingWhatsAppText,
  buildReturnReminderMessage,
  isVehicleDoubleBooked,
  bookingOverlaps,
} from '@/lib/morocco';
import { api } from '@/lib/api';
import { parseBookingPage } from '@/lib/booking/laravel-mirror';
import { listPath } from '@/lib/query/page';
import { useDebouncedSearch, useSectionParams } from '@/lib/query/table-params';
import { useTenantQuery } from '@/lib/query/useTenantQuery';
import { formatMoney } from '@/lib/currency';
import { deskReceiptDocument, deskReceipt } from '@/lib/desk-receipt';
import { DeskTable } from './DeskTable';
import { ReservationCalendar } from './ReservationCalendar';
import { SlideOver } from './SlideOver';
import { bookingTone, statusToneClass, useDeskToast } from './desk-feedback';
import { ListCapBanner } from './ListCapBanner';
import { RecordPager } from './RecordPager';
import {
  Calendar as CalendarIcon,
  List as ListIcon,
  Pencil,
  Plus,
  MessageCircle,
  Bell,
  ArrowRight,
  Ban,
  Receipt,
} from 'lucide-react';

interface Props {
  vehicles: Vehicle[];
  onAddBooking(b: Booking): void | Promise<boolean | void>;
  onUpdateBooking(b: Booking): void | Promise<boolean>;
  onUpdateBookingStatus(id: string, status: Booking['status']): void;
  onApplyStatus?(ids: string[], status: Booking['status']): Promise<boolean>;
  loadError?: boolean;
  onRetry?(): void;
  currency?: string | null;
  headerQuery?: string;
  locations?: string[];
  whatsappTemplate?: string;
  agencyName?: string;
  requestCreate?: number;
}

type StatusFilter = 'all' | Booking['status'];

const STATUSES: Booking['status'][] = ['pending', 'confirmed', 'active', 'returned', 'completed', 'cancelled'];

const BOOKING_COLUMNS = [
  { id: 'client', label: 'Client', sortable: true },
  { id: 'vehicle', label: 'Vehicle' },
  { id: 'schedule', label: 'Schedule', sortable: true },
  { id: 'location', label: 'Location' },
  { id: 'status', label: 'Status', sortable: true },
  { id: 'balance', label: 'Balance' },
];
const BOOKING_SORT: Record<string, string> = {
  client: 'customer_name',
  schedule: 'pickup_date',
  status: 'status',
};

function monthBounds(year: number, month: number): { from: string; to: string } {
  const from = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const last = new Date(year, month + 1, 0).getDate();
  const to = `${year}-${String(month + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { from, to };
}

function calcDays(pickup: string, drop: string) {
  if (!pickup || !drop) return 1;
  const ms = new Date(drop).getTime() - new Date(pickup).getTime();
  if (isNaN(ms)) return 1;
  return Math.max(1, Math.ceil(ms / 86400000));
}

interface FormState {
  vehicleId: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  driverLicense: string;
  pickupLocation: string;
  dropoffLocation: string;
  pickupDate: string;
  pickupTime: string;
  dropoffDate: string;
  dropoffTime: string;
  dailyRate: number;
  avance: number;
  caution: number;
  cautionType: NonNullable<Booking['cautionType']>;
  paymentMethod: NonNullable<Booking['paymentMethod']>;
  notes: string;
}

const EMPTY_FORM: FormState = {
  vehicleId: '',
  customerName: '',
  customerPhone: '',
  customerEmail: '',
  driverLicense: '',
  pickupLocation: MOROCCAN_LOCATIONS[0],
  dropoffLocation: MOROCCAN_LOCATIONS[0],
  pickupDate: '',
  pickupTime: '10:00',
  dropoffDate: '',
  dropoffTime: '10:00',
  dailyRate: 0,
  avance: 0,
  caution: 0,
  cautionType: 'cash',
  paymentMethod: 'cash',
  notes: '',
};

const inputCls =
  'w-full rounded-2xl border border-line bg-shell px-3 py-2 text-xs text-ink outline-none focus:border-[#FF8A00]';
const labelCls = 'mb-1 block text-xs font-semibold text-muted';

export default function BookingsModule({
  vehicles,
  onAddBooking,
  onUpdateBooking,
  onUpdateBookingStatus,
  onApplyStatus,
  onRetry,
  currency,
  locations = MOROCCAN_LOCATIONS,
  whatsappTemplate = '',
  agencyName = 'fleetava',
  requestCreate = 0,
}: Props) {
  const cur = currency ?? 'MAD';
  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  const table = useSectionParams('bookings');
  const search = useDebouncedSearch('bookings', table.search, table.write);
  const statusFilter = (table.read('status', 'all') || 'all') as StatusFilter;
  const vehicleFilter = table.read('vehicle', 'all');
  const pickupFrom = table.read('from');
  const locationFilter = table.read('location', 'all');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const notify = useDeskToast();
  const [batchBusy, setBatchBusy] = useState(false);
  const [month, setMonth] = useState(() => {
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth() };
  });
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Booking | null>(null);
  const [serverQuote, setServerQuote] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'vehicle' | 'name' | 'phone' | 'dates' | 'conflict', string>>>({});
  const [saving, setSaving] = useState(false);
  const places = useRef(locations);
  places.current = locations;
  useEffect(() => {
    if (!requestCreate) return;
    const office = places.current;
    setEditing(null);
    setServerQuote(null);
    setForm({ ...EMPTY_FORM, pickupLocation: office[0] ?? '', dropoffLocation: office[0] ?? '' });
    setFieldErrors({});
    setShowModal(true);
  }, [requestCreate]);

  const vehicleMap = useMemo(() => new Map(vehicles.map((v) => [v.id, v])), [vehicles]);

  const bounds = monthBounds(month.y, month.m);
  const listQuery = useTenantQuery(
    ['bookings', 'list', table.page, table.search, table.sort, table.direction, statusFilter, vehicleFilter, pickupFrom, locationFilter],
    listPath('/api/bookings', {
      page: table.page,
      perPage: 20,
      sort: table.sort || 'created_at',
      direction: table.direction || 'desc',
      q: table.search,
      status: statusFilter,
      vehicleId: vehicleFilter,
      from: pickupFrom,
      pickupLocation: locationFilter,
    }),
    parseBookingPage,
    view === 'list',
  );
  const calendarQuery = useTenantQuery(
    ['bookings', 'calendar', bounds.from, bounds.to],
    listPath('/api/bookings', {
      page: 1,
      perPage: 50,
      sort: 'pickup_date',
      direction: 'asc',
      from: bounds.from,
      to: bounds.to,
    }),
    parseBookingPage,
    view === 'calendar',
  );
  const listRows = (listQuery.data?.data ?? []) as Booking[];
  const calendarRows = (calendarQuery.data?.data ?? []) as Booking[];
  const visibleList = view === 'list'
    ? listRows
    : (!selectedDay ? calendarRows : calendarRows.filter((booking) => bookingOverlaps(selectedDay, selectedDay, booking.pickupDate, booking.dropoffDate)));
  const activeQuery = view === 'list' ? listQuery : calendarQuery;

  const formDays = calcDays(form.pickupDate, form.dropoffDate);
  const formTotal = editing && serverQuote != null ? serverQuote : formDays * (Number(form.dailyRate) || 0);

  useEffect(() => {
    if (!editing || !form.vehicleId || !form.pickupDate || !form.dropoffDate || form.dropoffDate < form.pickupDate) {
      setServerQuote(null);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void api<{ totalPrice: number }>('/api/quote', {
        method: 'POST',
        body: JSON.stringify({
          vehicleId: form.vehicleId,
          pickupDate: form.pickupDate,
          dropoffDate: form.dropoffDate,
          protectionPlan: editing.protectionPlan,
          selectedAddOns: editing.selectedAddOns,
          promoCode: editing.promoCode ?? '',
        }),
        signal: controller.signal,
      }).then((quote) => setServerQuote(quote.totalPrice)).catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setServerQuote(null);
      });
    }, 250);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [editing, form.vehicleId, form.pickupDate, form.dropoffDate]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const handleVehicleChange = (id: string) => {
    const v = vehicleMap.get(id);
    setForm((f) => ({ ...f, vehicleId: id, dailyRate: v ? v.pricePerDay : f.dailyRate }));
  };

  function openEdit(booking: Booking) {
    setEditing(booking);
    void api<Booking>(`/api/bookings/${booking.id}`).then((full) => {
      setEditing((current) => (current?.id === booking.id ? full : current));
    }).catch(() => undefined);
    setServerQuote(booking.totalPrice);
    setForm({
      vehicleId: booking.vehicleId,
      customerName: booking.customerName,
      customerPhone: booking.customerPhone,
      customerEmail: booking.customerEmail,
      driverLicense: booking.driverLicense,
      pickupLocation: booking.pickupLocation,
      dropoffLocation: booking.dropoffLocation,
      pickupDate: booking.pickupDate,
      pickupTime: booking.pickupTime || '10:00',
      dropoffDate: booking.dropoffDate,
      dropoffTime: booking.dropoffTime || '10:00',
      dailyRate: booking.dailyRate,
      avance: booking.avanceAmount ?? 0,
      caution: booking.cautionAmount ?? 0,
      cautionType: booking.cautionType ?? 'cash',
      paymentMethod: booking.paymentMethod ?? 'cash',
      notes: booking.notes ?? '',
    });
    setFieldErrors({});
    setShowModal(true);
  }

  const handleSave = async () => {
    const nextErrors: Partial<Record<'vehicle' | 'name' | 'phone' | 'dates' | 'conflict', string>> = {};
    if (!form.vehicleId) nextErrors.vehicle = 'Choose a vehicle.';
    if (!form.customerName.trim()) nextErrors.name = 'Enter the client name.';
    if (!form.customerPhone.trim()) nextErrors.phone = 'Enter a phone number.';
    if (!form.pickupDate || !form.dropoffDate) nextErrors.dates = 'Pickup and dropoff dates are required.';
    else if (form.dropoffDate < form.pickupDate) nextErrors.dates = 'Dropoff must be on or after pickup.';
    const conflict = form.vehicleId && form.pickupDate && form.dropoffDate
      ? isVehicleDoubleBooked(form.vehicleId, form.pickupDate, form.dropoffDate, visibleList, editing?.id)
      : null;
    if (conflict) nextErrors.conflict = `Already booked by ${conflict.customerName} (${conflict.pickupDate} → ${conflict.dropoffDate}).`;
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !form.vehicleId) return;
    if (editing) {
      setSaving(true);
      try {
        const saved = await Promise.resolve(onUpdateBooking({
          ...editing,
          vehicleId: form.vehicleId,
          customerName: form.customerName.trim(),
          customerEmail: form.customerEmail.trim(),
          customerPhone: form.customerPhone.trim(),
          driverLicense: form.driverLicense.trim(),
          pickupLocation: form.pickupLocation,
          dropoffLocation: form.dropoffLocation,
          pickupDate: form.pickupDate,
          pickupTime: form.pickupTime,
          dropoffDate: form.dropoffDate,
          dropoffTime: form.dropoffTime,
          avanceAmount: Number(form.avance) || 0,
          cautionAmount: Number(form.caution) || 0,
          cautionType: form.cautionType,
          paymentMethod: form.paymentMethod,
          notes: form.notes.trim(),
        }));
        if (saved === false) return;
        setShowModal(false);
        setEditing(null);
        notify('Booking updated.');
      } finally {
        setSaving(false);
      }
      return;
    }
    const v = vehicleMap.get(form.vehicleId);
    const days = calcDays(form.pickupDate, form.dropoffDate);
    const total = days * (Number(form.dailyRate) || 0);
    const b: Booking = {
      id: `DX-${Date.now().toString().slice(-6)}`,
      vehicleId: form.vehicleId,
      vehicleName: v ? `${v.brand} ${v.model}` : 'Vehicle',
      vehicleCategory: v?.category ?? 'Economy',
      vehiclePrice: (v?.pricePerDay ?? Number(form.dailyRate)) || 0,
      customerName: form.customerName.trim(),
      customerEmail: form.customerEmail.trim(),
      customerPhone: form.customerPhone.trim(),
      driverLicense: form.driverLicense.trim(),
      pickupLocation: form.pickupLocation,
      dropoffLocation: form.dropoffLocation,
      pickupDate: form.pickupDate,
      pickupTime: form.pickupTime,
      dropoffDate: form.dropoffDate,
      dropoffTime: form.dropoffTime,
      rentalDays: days,
      dailyRate: Number(form.dailyRate) || 0,
      protectionPlan: 'basic',
      protectionPrice: 0,
      selectedAddOns: [],
      addOnsPrice: 0,
      subtotal: total,
      discount: 0,
      totalPrice: total,
      status: 'pending',
      createdAt: new Date().toISOString(),
      avanceAmount: Number(form.avance) || 0,
      cautionAmount: Number(form.caution) || 0,
      cautionType: form.cautionType,
      paymentMethod: form.paymentMethod,
      notes: form.notes.trim(),
    };
    setSaving(true);
    try {
      const saved = await Promise.resolve(onAddBooking(b));
      if (saved === false) return;
      setForm({ ...EMPTY_FORM, pickupLocation: locations[0] ?? '', dropoffLocation: locations[0] ?? '' });
      setFieldErrors({});
      setShowModal(false);
      notify('Booking saved.');
    } finally {
      setSaving(false);
    }
  };

  const nextStep = (b: Booking): Booking['status'] | null => {
    const i = (PIPELINE_STEPS as readonly string[]).indexOf(b.status);
    if (i === -1 || i >= PIPELINE_STEPS.length - 1) return null;
    return PIPELINE_STEPS[i + 1] as Booking['status'];
  };

  const total = activeQuery.data?.meta.total ?? 0;
  const filtersActive = table.search !== '' || statusFilter !== 'all' || vehicleFilter !== 'all' || pickupFrom !== '' || locationFilter !== 'all';
  const listKind = activeQuery.isLoading ? 'loading' as const
    : activeQuery.isError && !activeQuery.data ? 'error' as const
    : total === 0 && filtersActive ? 'filtered' as const
    : total === 0 ? 'empty' as const
    : 'ready' as const;
  const visibleIds = visibleList.map((booking) => booking.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  function toggleVisibleBookings() {
    setSelected((current) => {
      const next = new Set(current);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }

  function toggleBooking(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function applyBatch(status: 'confirmed' | 'cancelled') {
    if (!onApplyStatus || batchBusy) return;
    const ids = visibleList
      .filter((booking) => selected.has(booking.id) && (status === 'confirmed'
        ? booking.status === 'pending'
        : booking.status !== 'completed' && booking.status !== 'cancelled'))
      .map((booking) => booking.id);
    if (ids.length === 0) return;
    setBatchBusy(true);
    const saved = await onApplyStatus(ids, status);
    setBatchBusy(false);
    if (!saved) {
      notify('Could not update those bookings.', 'error');
      return;
    }
    setSelected(new Set());
    notify(status === 'confirmed' ? `Confirmed ${ids.length}.` : `Cancelled ${ids.length}.`);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex rounded-2xl border border-line bg-card p-1 text-xs font-semibold">
          <button
            onClick={() => setView('calendar')}
            className={`flex items-center gap-1 rounded-xl px-3 py-1.5 ${view === 'calendar' ? 'bg-ink text-shell' : 'text-muted'}`}
          >
            <CalendarIcon size={13} /> Calendar
          </button>
          <button
            onClick={() => setView('list')}
            className={`flex items-center gap-1 rounded-xl px-3 py-1.5 ${view === 'list' ? 'bg-ink text-shell' : 'text-muted'}`}
          >
            <ListIcon size={13} /> List
          </button>
        </div>
        <button
          onClick={() => { setForm({ ...EMPTY_FORM, pickupLocation: locations[0] ?? '', dropoffLocation: locations[0] ?? '' }); setFieldErrors({}); setShowModal(true); }}
          className="flex items-center gap-1 rounded-2xl bg-[#FF8A00] px-4 py-2 text-xs font-bold text-slate-950 hover:opacity-90"
        >
          <Plus size={14} /> New booking
        </button>
      </div>

      {view === 'calendar' && (
        <ReservationCalendar
          year={month.y}
          month={month.m}
          bookings={calendarRows}
          fleetCount={vehicles.length}
          currency={cur}
          selectedDay={selectedDay}
          onSelectDay={setSelectedDay}
          onPrev={() => setMonth((current) => (current.m === 0 ? { y: current.y - 1, m: 11 } : { y: current.y, m: current.m - 1 }))}
          onNext={() => setMonth((current) => (current.m === 11 ? { y: current.y + 1, m: 0 } : { y: current.y, m: current.m + 1 }))}
          onToday={() => {
            const now = new Date();
            setMonth({ y: now.getFullYear(), m: now.getMonth() });
            setSelectedDay(null);
          }}
        />
      )}

      <DeskTable
        storageKey="fleetava-desk-booking-columns"
        columns={BOOKING_COLUMNS}
        filters={
          <div className="grid gap-2 sm:grid-cols-4">
            <select value={statusFilter} onChange={(event) => table.write({ status: event.target.value })} className={inputCls} aria-label="Status">
              <option value="all">All statuses</option>
              {STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
            <input type="date" value={pickupFrom} onChange={(event) => table.write({ from: event.target.value })} className={inputCls} aria-label="Pickup from" />
            <select value={locationFilter} onChange={(event) => table.write({ location: event.target.value })} className={inputCls} aria-label="Location">
              <option value="all">All locations</option>
              {locations.map((location) => <option key={location} value={location}>{location}</option>)}
            </select>
            <select value={vehicleFilter} onChange={(event) => table.write({ vehicle: event.target.value })} className={inputCls} aria-label="Vehicle">
              <option value="all">All vehicles</option>
              {vehicles.map((vehicle) => <option key={vehicle.id} value={vehicle.id}>{vehicle.brand} {vehicle.model}</option>)}
            </select>
          </div>
        }
        selectedCount={selected.size}
        allVisibleSelected={allVisibleSelected}
        onToggleVisible={toggleVisibleBookings}
        onClearSelection={() => setSelected(new Set())}
        batch={
          <>
            <button type="button" disabled={batchBusy} onClick={() => void applyBatch('confirmed')} className="rounded-full bg-brand px-3 py-1 font-semibold text-slate-950 disabled:opacity-50">
              Confirm
            </button>
            <button type="button" disabled={batchBusy} onClick={() => void applyBatch('cancelled')} className="rounded-full bg-danger px-3 py-1 font-semibold text-white disabled:opacity-50">
              Cancel
            </button>
          </>
        }
        search={
          <input value={search.draft} onChange={(event) => search.onSearch(event.target.value)} placeholder="Search client, phone, or booking" aria-label="Search bookings" className={inputCls} />
        }
        refreshing={activeQuery.isFetching && Boolean(activeQuery.data)}
        refreshError={activeQuery.isError && Boolean(activeQuery.data)}
        sort={table.sort ? { id: Object.entries(BOOKING_SORT).find(([, column]) => column === table.sort)?.[0] ?? '', direction: table.direction === 'asc' ? 'asc' : 'desc' } : undefined}
        onSort={(columnId) => {
          const column = BOOKING_SORT[columnId];
          if (!column) return;
          table.write({ sort: column, dir: table.sort === column && table.direction === 'asc' ? 'desc' : 'asc' });
        }}
        listState={listKind}
        onRetry={() => { void activeQuery.refetch(); onRetry?.(); }}
        emptyTitle="No bookings yet."
        filteredTitle="No rows match these filters."
        errorTitle="The booking list could not be loaded."
        notice={null}
        onDismissNotice={() => undefined}
      >
        {(shown) => visibleList.map((booking) => {
          const advance = booking.avanceAmount ?? 0;
          const balance = booking.totalPrice - advance;
          const next = nextStep(booking);
          return (
            <tr key={booking.id} className="border-t border-line">
              <td className="px-3 py-2">
                <input type="checkbox" checked={selected.has(booking.id)} onChange={() => toggleBooking(booking.id)} aria-label={`Select ${booking.id}`} />
              </td>
              {shown('client') && (
                <td className="px-3 py-2">
                  <p className="font-semibold">{booking.customerName}</p>
                  <p className="text-muted">{booking.customerPhone}</p>
                </td>
              )}
              {shown('vehicle') && <td className="px-3 py-2">{booking.vehicleName}</td>}
              {shown('schedule') && (
                <td className="px-3 py-2">
                  <p>{booking.pickupDate} {booking.pickupTime}</p>
                  <p className="text-muted">{booking.dropoffDate} {booking.dropoffTime}</p>
                </td>
              )}
              {shown('location') && <td className="px-3 py-2">{booking.pickupLocation}</td>}
              {shown('status') && (
                <td className="px-3 py-2">
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${statusToneClass(bookingTone(booking.status))}`}>{booking.status}</span>
                </td>
              )}
              {shown('balance') && (
                <td className="px-3 py-2">
                  <p>{formatMoney(booking.totalPrice, cur)}</p>
                  <p className={balance > 0 ? 'text-warning' : 'text-success'}>Due {formatMoney(balance, cur)}</p>
                </td>
              )}
              <td className="px-3 py-2">
                <div className="flex justify-end gap-1">
                  {next && (
                    <button type="button" onClick={() => onUpdateBookingStatus(booking.id, next)} className="rounded-lg border border-line p-1.5" aria-label={`Advance to ${next}`}>
                      <ArrowRight size={12} />
                    </button>
                  )}
                  <button type="button" onClick={() => openEdit(booking)} className="rounded-lg border border-line p-1.5" aria-label="Edit dates">
                    <Pencil size={12} />
                  </button>
                  {booking.status !== 'completed' && booking.status !== 'cancelled' && (
                    <button type="button" onClick={() => onUpdateBookingStatus(booking.id, 'cancelled')} className="rounded-lg border border-danger/40 p-1.5 text-danger" aria-label="Cancel booking">
                      <Ban size={12} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const popup = window.open('', '_blank', 'noopener,noreferrer,width=480,height=720');
                      if (!popup) return;
                      popup.document.write(deskReceiptDocument(deskReceipt(booking), agencyName));
                      popup.document.close();
                    }}
                    className="rounded-lg border border-line p-1.5"
                    aria-label="Receipt"
                  >
                    <Receipt size={12} />
                  </button>
                  <a href={buildWhatsAppLink(booking.customerPhone, bookingWhatsAppText(booking, whatsappTemplate, agencyName, cur))} target="_blank" rel="noreferrer" className="rounded-lg border border-line p-1.5 text-success" aria-label="WhatsApp">
                    <MessageCircle size={12} />
                  </a>
                  <a href={buildWhatsAppLink(booking.customerPhone, buildReturnReminderMessage(booking))} target="_blank" rel="noreferrer" className="rounded-lg border border-line p-1.5" aria-label="Reminder">
                    <Bell size={12} />
                  </a>
                </div>
              </td>
            </tr>
          );
        })}
      </DeskTable>
      {view === 'calendar' && calendarQuery.data && calendarQuery.data.meta.total > calendarRows.length && (
        <ListCapBanner caps={[{ label: 'bookings this month', shown: calendarRows.length, total: calendarQuery.data.meta.total }]} />
      )}
      {view === 'list' && (
        <RecordPager
          page={listQuery.data?.meta.page ?? table.page}
          pages={Math.max(1, Math.ceil(total / (listQuery.data?.meta.perPage || 20)))}
          total={listQuery.data?.meta.total ?? 0}
          onPage={(next) => table.write({ page: String(next) }, false)}
        />
      )}

      <SlideOver
        open={showModal}
        title={`${editing ? 'Edit booking' : 'New booking'} · ${formDays}d · ${formatMoney(formTotal, cur)}`}
        onClose={() => { if (!saving) setShowModal(false); }}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowModal(false)} disabled={saving} className="rounded-2xl border border-line px-4 py-2 text-xs font-bold text-ink disabled:opacity-50">Cancel</button>
            <button type="button" onClick={() => void handleSave()} disabled={saving} className="rounded-2xl bg-brand px-4 py-2 text-xs font-bold text-slate-950 disabled:opacity-50">{saving ? 'Saving…' : `Save booking · ${formatMoney(formTotal, cur)}`}</button>
          </div>
        }
      >
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelCls}>Vehicle *</label>
                <select value={form.vehicleId} onChange={(e) => handleVehicleChange(e.target.value)} className={inputCls}>
                  <option value="">Select vehicle…</option>
                  {vehicles.map((v) => <option key={v.id} value={v.id}>{v.brand} {v.model} · {v.licensePlate} · {formatMoney(v.pricePerDay, cur)}/d</option>)}
                </select>
                {fieldErrors.vehicle && <p className="mt-1 text-[11px] font-semibold text-danger">{fieldErrors.vehicle}</p>}
              </div>
              <div><label className={labelCls}>Client name *</label><input value={form.customerName} onChange={(e) => set('customerName', e.target.value)} className={inputCls} />{fieldErrors.name && <p className="mt-1 text-[11px] font-semibold text-danger">{fieldErrors.name}</p>}</div>
              <div><label className={labelCls}>Phone *</label><input value={form.customerPhone} onChange={(e) => set('customerPhone', e.target.value)} className={inputCls} />{fieldErrors.phone && <p className="mt-1 text-[11px] font-semibold text-danger">{fieldErrors.phone}</p>}</div>
              <div><label className={labelCls}>Email</label><input value={form.customerEmail} onChange={(e) => set('customerEmail', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Driver license</label><input value={form.driverLicense} onChange={(e) => set('driverLicense', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Pickup location</label>
                <select value={form.pickupLocation} onChange={(e) => set('pickupLocation', e.target.value)} className={inputCls}>
                  {locations.map((location) => <option key={location} value={location}>{location}</option>)}
                </select></div>
              <div><label className={labelCls}>Dropoff location</label>
                <select value={form.dropoffLocation} onChange={(e) => set('dropoffLocation', e.target.value)} className={inputCls}>
                  {locations.map((location) => <option key={location} value={location}>{location}</option>)}
                </select></div>
              <div><label className={labelCls}>Pickup date</label><input type="date" value={form.pickupDate} onChange={(e) => set('pickupDate', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Dropoff date</label><input type="date" value={form.dropoffDate} onChange={(e) => set('dropoffDate', e.target.value)} className={inputCls} />{(fieldErrors.dates || fieldErrors.conflict) && <p className="mt-1 text-[11px] font-semibold text-danger">{fieldErrors.dates || fieldErrors.conflict}</p>}</div>
              <div><label className={labelCls}>Pickup time</label><input type="time" value={form.pickupTime} onChange={(e) => set('pickupTime', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Dropoff time</label><input type="time" value={form.dropoffTime} onChange={(e) => set('dropoffTime', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Daily rate (MAD)</label><input type="number" min={0} value={form.dailyRate} onChange={(e) => set('dailyRate', Number(e.target.value))} className={inputCls} /></div>
              <div><label className={labelCls}>Avance (MAD)</label><input type="number" min={0} value={form.avance} onChange={(e) => set('avance', Number(e.target.value))} className={inputCls} /></div>
              <div><label className={labelCls}>Caution (MAD)</label><input type="number" min={0} value={form.caution} onChange={(e) => set('caution', Number(e.target.value))} className={inputCls} /></div>
              <div><label className={labelCls}>Caution type</label>
                <select value={form.cautionType} onChange={(e) => set('cautionType', e.target.value as FormState['cautionType'])} className={inputCls}>
                  <option value="cash">cash</option><option value="check">check</option>
                  <option value="card_pre_auth">card_pre_auth</option><option value="none">none</option>
                </select></div>
              <div><label className={labelCls}>Payment method</label>
                <select value={form.paymentMethod} onChange={(e) => set('paymentMethod', e.target.value as FormState['paymentMethod'])} className={inputCls}>
                  <option value="cash">cash</option><option value="transfer">transfer</option>
                  <option value="card">card</option><option value="mixed">mixed</option>
                </select></div>
              <div><label className={labelCls}>Reste estimé</label><input disabled value={formatMoney(Math.max(0, formTotal - (Number(form.avance) || 0)), cur)} className={`${inputCls} opacity-70`} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Notes</label><textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} className={inputCls} /></div>
            </div>
      </SlideOver>
    </div>
  );
}
